import { isApiError, unwrap } from "@sagot/api-client";
import { CONSOLE_PATHS, EMAIL_MAX_LENGTH, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useErrorMessage } from "../lib/api-errors";
import { clearPendingAuth, readPendingAuth, writePendingAuth } from "../session/pending-auth";
import { useApi } from "../session/session-provider";
import { RegisterAboutStep } from "./register-about-step";
import {
  FIELD_STEP,
  PASSWORD_ERROR_CODES,
  REGISTER_PARAMS,
  REGISTER_STEPS,
  type RegisterStep,
} from "./register-constants";
import { RegisterConsents } from "./register-consents";
import { RegisterDonePane } from "./register-done-pane";
import {
  clearDraft,
  furthestStep,
  parsePlan,
  parseStep,
  readDraft,
  readSignupPlan,
  resolveStep,
  stepNumber,
  writeDraft,
  writeSignupPlan,
  type RegisterDraft,
} from "./register-draft";
import { RegisterEmailStep } from "./register-email-step";
import { RegisterOrgStep } from "./register-org-step";
import { isEmail, isNameOk, isOrgOk } from "./register-rules";
import { RegisterShell } from "./register-shell";
import { RegisterVerifyPane } from "./register-verify-pane";
import { useCompleteSignIn } from "./use-sign-in";

interface StepError {
  step: RegisterStep;
  message: string;
}

/** `?email=` as it arrived (read once; it is then removed from the address bar). */
function readEntry(params: URLSearchParams): string | null {
  const raw = params.get(REGISTER_PARAMS.email);
  return raw === null ? null : raw.trim().slice(0, EMAIL_MAX_LENGTH);
}

function previousStep(step: RegisterStep): RegisterStep {
  return REGISTER_STEPS[Math.max(0, REGISTER_STEPS.indexOf(step) - 1)] ?? "email";
}

/**
 * Register wizard (WP-RD-10, D9): email, about you, organization, consents, then `POST /v1/auth/register`, the code pane
 * (`/v1/auth/verify-email`) and the ready pane. The step lives in `?step=`, the answers in memory and (without the password)
 * in sessionStorage, so Back and a refresh keep them. `?email=` fills and skips the first step; `?plan=` rides on to onboarding.
 */
export function RegisterPage() {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const completeSignIn = useCompleteSignIn();
  const [params, setParams] = useSearchParams();
  const [arrivedEmail] = useState(() => readEntry(params));
  const [draft, setDraft] = useState<RegisterDraft>(() => ({ ...readDraft(), ...(arrivedEmail ? { email: arrivedEmail } : {}) }));
  const [signedUp, setSignedUp] = useState<{ body: unknown } | null>(null);
  const [stepError, setStepError] = useState<StepError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);

  const patch = useCallback((changes: Partial<RegisterDraft>) => {
    setDraft((current) => ({ ...current, ...changes }));
  }, []);

  const go = useCallback(
    (next: RegisterStep, replace = false) => {
      setParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          updated.set(REGISTER_PARAMS.step, next);
          updated.delete(REGISTER_PARAMS.email);
          return updated;
        },
        { replace },
      );
    },
    [setParams],
  );

  const pending = readPendingAuth();
  const pendingEmail = pending?.kind === "verify-email" ? pending.email : null;
  const requested = parseStep(params.get(REGISTER_PARAMS.step));
  const furthest = furthestStep(draft, { pendingEmail, verified: signedUp !== null });
  const step: RegisterStep = signedUp ? "ready" : resolveStep(requested, furthest);
  const planFromUrl = parsePlan(params.get(REGISTER_PARAMS.plan));
  const plan = planFromUrl ?? readSignupPlan();

  // The pane slides in from the left when the visitor goes back (derived once per step, so typing does not replay it).
  const [direction, setDirection] = useState({ step, back: false });
  if (direction.step !== step) setDirection({ step, back: stepNumber(step) < stepNumber(direction.step) });

  // `?email=` is taken once: a valid address skips the first step, then it leaves the address bar.
  const entryHandled = useRef(false);
  useEffect(() => {
    if (entryHandled.current || arrivedEmail === null) return;
    entryHandled.current = true;
    const updated = new URLSearchParams(params);
    updated.delete(REGISTER_PARAMS.email);
    if (isEmail(arrivedEmail)) updated.set(REGISTER_PARAMS.step, "about");
    setParams(updated, { replace: true });
  }, [arrivedEmail, params, setParams]);

  // A deep link or refresh never lands past what is filled in.
  useEffect(() => {
    if (signedUp || (arrivedEmail !== null && !entryHandled.current)) return;
    if (step !== requested) go(step, true);
  }, [arrivedEmail, go, requested, signedUp, step]);

  useEffect(() => {
    if (planFromUrl) writeSignupPlan(planFromUrl);
  }, [planFromUrl]);

  useEffect(() => {
    if (!signedUp) writeDraft(draft);
  }, [draft, signedUp]);

  const reject = (at: RegisterStep, text: string) => {
    setStepError({ step: at, message: text });
    if (at !== step) go(at);
  };

  const failSignUp = (caught: unknown) => {
    setCaptchaReset((n) => n + 1);
    if (!isApiError(caught)) {
      reject("consents", message(caught));
      return;
    }
    if (PASSWORD_ERROR_CODES.includes(caught.code)) {
      reject("about", message(caught));
      return;
    }
    const field = Object.keys(caught.fields).find((name) => FIELD_STEP[name] !== undefined);
    const owner = field ? FIELD_STEP[field] : undefined;
    reject(owner ?? "consents", (field ? caught.fields[field] : undefined) ?? message(caught));
  };

  const signUp = async (captchaToken: string) => {
    if (!draft.terms || !draft.dpa) return;
    const email = draft.email.trim();
    setSubmitting(true);
    setStepError(null);
    try {
      await unwrap(
        api.POST("/v1/auth/register", {
          body: {
            name: draft.name.trim(),
            email,
            password: draft.password,
            org_name: draft.orgName.trim(),
            org_type: draft.industry ?? "other",
            size: draft.size,
            country: draft.country,
            consents: { terms: draft.terms, dpa: draft.dpa, marketing: draft.marketing },
            captcha_token: captchaToken,
          },
        }),
      );
      writePendingAuth({ kind: "verify-email", email });
      go("verify");
    } catch (caught) {
      failSignUp(caught);
    } finally {
      setSubmitting(false);
    }
  };

  // Where "Open the console" goes. `/register` sits under PublicOnly, which sends a signed-in visitor to `?next=` (else home) the
  // moment the session exists: the same target is put in the address bar first, so the two redirects agree.
  const onboardingTarget = plan ? `${CONSOLE_PATHS.onboarding}?${REGISTER_PARAMS.plan}=${plan}` : CONSOLE_PATHS.onboarding;

  const onVerified = (body: unknown) => {
    clearDraft();
    setSignedUp({ body });
    setParams(
      (previous) => {
        const updated = new URLSearchParams(previous);
        updated.set(REGISTER_PARAMS.step, "ready");
        updated.set(REGISTER_PARAMS.next, onboardingTarget);
        updated.delete(REGISTER_PARAMS.email);
        return updated;
      },
      { replace: true },
    );
  };

  const openConsole = async () => {
    if (!signedUp) return;
    setOpening(true);
    setOpenError(null);
    try {
      await completeSignIn(signedUp.body, { email: draft.email.trim(), next: onboardingTarget });
    } catch (caught) {
      setOpenError(message(caught));
      setOpening(false);
    }
  };

  const errorAt = (at: RegisterStep): string | null => (stepError?.step === at ? stepError.message : null);
  const dismissError = () => {
    setStepError(null);
  };
  const back = direction.back;
  const onBack = () => {
    go(previousStep(step));
  };

  return (
    <RegisterShell position={stepNumber(step)} title={t("auth.register.title", { product: PRODUCT_NAME })}>
      {step === "email" ? (
        <RegisterEmailStep
          email={draft.email}
          onEmailChange={(email) => {
            patch({ email });
          }}
          plan={planFromUrl}
          back={back}
          onNext={() => {
            go("about");
          }}
        />
      ) : null}
      {step === "about" ? (
        <RegisterAboutStep
          name={draft.name}
          password={draft.password}
          onChange={patch}
          error={errorAt("about")}
          onDismissError={dismissError}
          resumed={draft.password === "" && isNameOk(draft.name) && isOrgOk(draft.orgName, draft.industry)}
          back={back}
          onBack={onBack}
          onNext={() => {
            go("organization");
          }}
        />
      ) : null}
      {step === "organization" ? (
        <RegisterOrgStep
          orgName={draft.orgName}
          industry={draft.industry}
          size={draft.size}
          country={draft.country}
          onChange={patch}
          error={errorAt("organization")}
          back={back}
          onBack={onBack}
          onNext={() => {
            go("consents");
          }}
        />
      ) : null}
      {step === "consents" ? (
        <RegisterConsents
          terms={draft.terms}
          dpa={draft.dpa}
          marketing={draft.marketing}
          onChange={patch}
          onSubmit={(token) => void signUp(token)}
          submitting={submitting}
          error={errorAt("consents")}
          onDismissError={dismissError}
          captchaReset={captchaReset}
          back={back}
          onBack={onBack}
        />
      ) : null}
      {step === "verify" ? (
        <RegisterVerifyPane
          email={draft.email.trim()}
          back={back}
          onChangeEmail={() => {
            clearPendingAuth();
            go("email");
          }}
          onVerified={onVerified}
        />
      ) : null}
      {step === "ready" ? <RegisterDonePane name={draft.name} orgName={draft.orgName} opening={opening} error={openError} onOpen={() => void openConsole()} /> : null}
    </RegisterShell>
  );
}
